import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ChargeListItem.scss';
import {
  Charge,
  INVOICEID_NO_INVOICE,
} from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';
import { SupportedLocaleEnum } from 'localization';

import { Flex, IconButton } from 'components';
import { ChargeItemsTable } from '../../ChargeItemsTable/ChargeItemsTable';
import { ChargeSummary } from '../../ChargeSummary/ChargeSummary';
import { checkChargeVoidable } from 'lane-shared/domains/billingPayments/hooks/helpers/checkChargeVoidable';
import { ConfirmationModal } from 'components/general/ConfirmationModal';
import { useBillingPaymentsPermissions } from 'lane-shared/domains/billingPayments/hooks';
import { useSrInvoiceBilledStatusEnabled } from 'react-hooks';
import { isManagedInvoiceStatus } from 'domains/billingAndPayments/invoiceBilling/invoiceStatusPresentation';

export type ChargeStatusVisibility = 'always' | 'yes' | 'no';

export function ChargeListItem({
  charge,
  currency,
  locale,
  voidCharge,
  showSRChargeStatus = 'always',
  channelSlug,
  showVoidOption = true,
}: {
  charge: Charge;
  currency: SupportedPaymentCurrency;
  locale: SupportedLocaleEnum;
  voidCharge: () => void;
  showSRChargeStatus: ChargeStatusVisibility;
  channelSlug?: string;
  showVoidOption?: boolean;
}) {
  const { t } = useTranslation();

  const [voidChargeModalOpen, setVoidChargeModalOpen] = useState(false);
  const isChargeVoidable = checkChargeVoidable(charge);

  const { canVoidCharges } = useBillingPaymentsPermissions();

  const handleVoidCharge = async () => {
    voidCharge();
    setVoidChargeModalOpen(false);
    await window.Toast.show(t('abp.charges.chargeManager.draft.voidToast'));
  };

  const isChargeVoided = charge.status === 'VOIDED';
  const hasInvoice = charge.invoiceId !== INVOICEID_NO_INVOICE;
  const isBilledStatusEnabled = useSrInvoiceBilledStatusEnabled();
  const isInvoiceManaged = isManagedInvoiceStatus(
    charge.invoice?.status ?? '',
    isBilledStatusEnabled
  );

  const showStatus =
    isChargeVoided ||
    (!isInvoiceManaged &&
      (showSRChargeStatus === 'always' ||
        (showSRChargeStatus === 'yes' && hasInvoice)));

  return (
    <div id={charge.id}>
      <Flex
        className={styles.Charge}
        gap={5}
        data-test={`billing-charge-item-${charge.id}`}
      >
        <ChargeItemsTable
          charge={charge}
          locale={locale}
          currency={currency}
          showStatus={showStatus}
          channelSlug={channelSlug}
        />
        <ChargeSummary items={charge.items} locale={locale} currency={currency}>
          {showVoidOption && canVoidCharges && isChargeVoidable ? (
            <Flex width="full">
              <IconButton
                className={styles.VoidChargeButton}
                size="large"
                onClick={() => setVoidChargeModalOpen(true)}
                text={t(
                  'abp.charges.chargeManager.draftCharge.voidChargeButton'
                )}
                testId={`void-product-button-${charge.name}`}
              />
            </Flex>
          ) : undefined}
        </ChargeSummary>
        <ConfirmationModal
          isOpen={voidChargeModalOpen}
          onClose={() => setVoidChargeModalOpen(false)}
          handleConfirm={handleVoidCharge}
          title={t('abp.charges.chargeManager.draftCharge.voidModal.title')}
          description={t(
            'abp.charges.chargeManager.draftCharge.voidModal.description'
          )}
          cancelText={t(
            'abp.charges.chargeManager.draftCharge.voidModal.cancelButton'
          )}
          confirmText={t(
            'abp.charges.chargeManager.draftCharge.voidModal.confirmButton'
          )}
          testId="void-charge-modal"
        />
      </Flex>
    </div>
  );
}
